import { useState } from 'react';
import {
  Award,
  ExpandIcon,
  ExternalLinkIcon,
  LinkedinIcon,
} from 'lucide-react';
import { Button, Modal, ModalContent } from '@sandtech-hub-monorepo/ui-kit';
import { IssuedCourseBadgeFragment } from '@sandtech-hub-monorepo/ehub-client';
import { formatDate } from '../../../dateFormat';

type Props = {
  courseName: string;
  badge: IssuedCourseBadgeFragment;
};

export const CourseBadgeCard = ({ courseName, badge }: Props) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const imageUrl = badge.badgeDefinition?.imageUrl;
  const badgeName = badge.badgeDefinition?.name ?? 'Course Badge';

  if (!imageUrl) return null;

  return (
    <>
      <div className="flex flex-col md:flex-row items-start gap-3 md:gap-4 p-3 sm:p-4 rounded-lg bg-background-tertiary border border-border">
        <button
          type="button"
          onClick={() => setIsPreviewOpen(true)}
          aria-label={`View ${badgeName} badge larger`}
          className="relative block w-full md:w-40 lg:w-48 rounded-md overflow-hidden shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <img
            src={imageUrl}
            alt={`${badgeName} badge for ${courseName}`}
            className="w-full h-auto border border-border rounded-md bg-background"
            width={1200}
            height={630}
            loading="lazy"
          />
          <div className="pointer-events-none absolute top-2 right-2 flex items-center gap-1.5 rounded-md bg-background/90 backdrop-blur-sm px-2 py-1 text-xs font-medium text-foreground shadow-sm">
            <ExpandIcon className="w-3 h-3" />
          </div>
        </button>
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">
            You earned this badge by completing this course.
          </p>
          <p className="text-xs text-muted-foreground">
            Issued {formatDate(badge.issuedAt)}
          </p>
          <div className="flex flex-wrap gap-2 mt-1">
            <Button asChild size="sm" className="gap-2 rounded-full">
              <a
                href={badge.linkedInShareUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LinkedinIcon className="w-4 h-4" />
                Share Badge
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="gap-2 rounded-full"
            >
              <a
                href={badge.verificationUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLinkIcon className="w-4 h-4" />
                View Badge
              </a>
            </Button>
          </div>
        </div>
      </div>

      <Modal open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <ModalContent className="w-[96vw] md:w-[min(96vw,760px)] max-w-none p-4 sm:p-6 overflow-hidden border-0 bg-background [&>div:nth-child(2)]:overflow-hidden [&>div:nth-child(2)]:m-0 [&>div:nth-child(2)]:p-0">
          <div className="bg-slate-900 rounded-2xl overflow-hidden px-4 sm:px-6 md:px-10 pt-8 sm:pt-10 pb-6 sm:pb-8 flex flex-col items-center gap-4 sm:gap-5 shadow-xl">
            <div className="flex items-center gap-2 border border-white/15 bg-white/5 rounded-full px-4 py-1.5">
              <Award className="w-3.5 h-3.5 text-primary" strokeWidth={2.5} />
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/90">
                Digital Credential
              </p>
            </div>

            <div className="relative flex items-center justify-center w-full">
              <div className="absolute w-72 h-72 sm:w-96 sm:h-96 bg-primary/25 blur-3xl rounded-full" />
              <img
                src={imageUrl}
                alt={`${badgeName} badge for ${courseName}`}
                className="relative w-full max-w-2xl h-auto object-contain drop-shadow-2xl"
                width={1200}
                height={630}
              />
            </div>

            <p className="text-sm text-white/70 text-center max-w-md">
              You earned this badge by completing this course.
            </p>
            <p className="text-xs text-white/45">
              Issued {formatDate(badge.issuedAt)}
            </p>
          </div>

          <div className="pt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="gap-2">
              <a
                href={badge.linkedInShareUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LinkedinIcon className="w-4 h-4" />
                Share Badge
              </a>
            </Button>
            <Button asChild variant="outline" className="gap-2">
              <a
                href={badge.verificationUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLinkIcon className="w-4 h-4" />
                View Badge
              </a>
            </Button>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};
